import CustomDialog from 'common/components/CustomDialog'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import {
  useMutateUpdateStockQuantity,
  useMutateUpdateProductIssue,
  useMutateUpdateTransferWarehouse,
} from 'modules/inventory/api/mutation'
import {
  Stock,
  UpdateInventoryStock,
  updateTransferWarehouse,
  Warehouse,
} from 'modules/inventory/api/type'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import {
  Avatar,
  Box,
  Card,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import { fNumber } from 'common/utils/formatNumber'
import { formatThaiShortBEWithTime } from 'modules/inventory/helper/format_date'

export type StockMode = 'product-issue' | 'warehouse-transfer' | 'edit-qty'

export default function StockDialog({
  open,
  onClose,
  onDone,          // ✅ new
  mode,
  stock,
  warehouses = [],
  pageSize,
}: {
  open: boolean
  onClose: () => void
  onDone?: () => void
  mode: StockMode
  stock: Stock
  warehouses?: Warehouse[]
  pageSize: number
}) {
  const { t } = useTranslation(['stock'])
  const [isLoading, setIsLoading] = useState(false)
  const updateStockQuantityMutation = useMutateUpdateStockQuantity(pageSize)
  const updateProductIssueMutation = useMutateUpdateProductIssue(pageSize)
  const updateTransferWarehouseMutation = useMutateUpdateTransferWarehouse(pageSize)

  type FormIssueAdjust = UpdateInventoryStock
  type FormMove = updateTransferWarehouse
  type FormValues = FormIssueAdjust | FormMove

  const schemaMove = Yup.object({
    destination_warehouse_id: Yup.string().required(t('validation.select.warehouse')),
    quantity: Yup.number().typeError(t('validation.enter.number')).required(t('validation.enter.quantity')).positive(t('validation.positive')),
  })

  const schemaIssueAdjust = Yup.object({
    quantity:
      mode === 'product-issue'
        ? Yup.number().typeError(t('validation.enter.number')).required(t('validation.enter.quantity')).positive(t('validation.positive'))
        : Yup.number()
          .typeError(t('validation.enter.number'))
          .required(t('validation.enter.quantity'))
          .test('not-zero', t('validation.not.zero'), (value) => value !== 0),
    remark: Yup.string().required(t('validation.enter.remark')),
  })

  const activeSchema = (mode === 'warehouse-transfer' ? schemaMove : schemaIssueAdjust) as Yup.AnyObjectSchema
  const resolver = useYupValidationResolver(activeSchema)

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver,
    defaultValues:
      mode === 'warehouse-transfer'
        ? ({ destination_warehouse_id: '', quantity: undefined as unknown as number } as FormMove)
        : ({ quantity: undefined as unknown as number, remark: '' } as FormIssueAdjust),
  })

  // TODO: ใส่ mutation จริงของคุณ
  // const transferMutation = useMutateTransferWarehouse();
  // const updateQtyMutation = useMutateUpdateInventoryStock();

  const onSubmit = async (values: FormValues) => {
    try {
      setIsLoading(true)
      if (mode === 'warehouse-transfer') {
        await updateTransferWarehouseMutation.mutateAsync({ body: values as FormMove, stockId: stock.id }).then(() => reset())
      } else if (mode === 'product-issue') {
        await updateProductIssueMutation.mutateAsync({ body: values as FormIssueAdjust, stockId: stock.id }).then(() => reset())
      } else if (mode === 'edit-qty') {
        await updateStockQuantityMutation.mutateAsync({ body: values as FormIssueAdjust, stockId: stock.id }).then(() => reset())
      }
      onDone?.()
    } finally {
      setIsLoading(false)
    }
  }

  const dialogTitle =
    mode === 'warehouse-transfer'
      ? t('dialog.edit.warehouse.stock.title')
      : mode === 'product-issue'
        ? t('dialog.product.issue.stock.title')
        : t('stock.adjust.qty')

  const ProductCard = (
    <Card
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1,
        mb: 2,
        bgcolor: '#F5F5F5',
        borderColor: '#EAEAEA',
        border: 'none',
        boxShadow: 'none',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          src={stock.product.image_url || undefined}
          alt={stock.product.name}
          variant="rounded"
          sx={{ width: 44, height: 44, borderRadius: 1.5, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
            <Typography variant="h5" fontWeight={700} noWrap>
              {stock.product.name}
            </Typography>
            {stock.is_active !== undefined && (
              <Chip
                label={stock.is_active ? t('stock.status.active') : t('stock.status.inactive')}
                size="small"
                variant="outlined"
                sx={{
                  ml: 1,
                  flexShrink: 0,
                  height: 22,
                  fontSize: 11,
                  fontWeight: 600,
                  borderColor: stock.is_active ? 'success.main' : 'grey.400',
                  color: stock.is_active ? 'success.dark' : 'text.secondary',
                  backgroundColor: stock.is_active ? '#E5FFF2' : null,
                  borderRadius: 1
                }}
              />
            )}
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {stock.lot_number && (
                <Chip
                label={`${t('dialog.stock.lot')}: ${stock.lot_number}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    flexShrink: 0,
                    height: 22,
                    fontSize: 12,
                    fontWeight: 500,
                    backgroundColor: 'white',
                    border: 0,
                    borderRadius: 1,
                  }}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {stock.lot_number && stock.created_at ? '  ' : ''}
                {stock.created_at ? `${t('dialog.stock.received')} ${formatThaiShortBEWithTime(stock.created_at)}` : ''}
              </Typography>
            </Stack>
            <Typography variant="caption" fontWeight={600} sx={{ flexShrink: 0, ml: 1 }}>
              {fNumber(stock.price_per_unit)} {t('bath')}/{stock.unit}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Card>
  )

  return (
    <CustomDialog
      open={open}
      closeDialog={onClose}
      title={dialogTitle}
      submitLabel={t('stock.save')}
      isSubmitLoading={isLoading}
      onSubmit={() => { }}
      formName="stock-form"
      enableCloseButton
      enableInLineWithTitle
      maxWidth="sm"
      fullWidth
      contentMinWidth={0}
    >
      {/* ✅ ห่อเป็นฟอร์มเพื่อให้ submit ผ่าน formName ได้ */}
      <form id="stock-form" onSubmit={handleSubmit(onSubmit)}>
        {ProductCard}

        {mode === 'warehouse-transfer' ? (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <label style={{ fontWeight: 600 }}>
                  <span style={{ color: 'red', marginRight: 4 }}>*</span>
                  {t('dialog.stock.warehouse')}
                </label>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  value={stock.warehouse.name}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <label style={{ fontWeight: 600 }}>
                  <span style={{ color: 'red', marginRight: 4 }}>*</span>
                  {t('dialog.stock.transfer')}
                </label>
                <Controller
                  name="destination_warehouse_id"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      select
                      size="small"
                      fullWidth
                      sx={{ mt: 0.5 }}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (selected === '' || selected == null)
                            return <span style={{ opacity: 0.6 }}>{t('please.input.warehouse')}</span>
                          const found = warehouses.find((u) => String(u.id) === String(selected))
                          return found ? found.name : ''
                        },
                      }}
                    >
                      {warehouses
                        .filter((w) => w.id !== stock.warehouse.id)
                        .map((w) => (
                          <MenuItem key={w.id} value={w.id}>
                            {w.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>

            <Box sx={{ mb: 1 }}>
              <label style={{ fontWeight: 600 }}>
                <span style={{ color: 'red', marginRight: 4 }}>*</span>
                {t('stock.history.quantity')}
              </label>
              <Controller
                name="quantity"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    sx={{ mt: 0.5 }}
                    placeholder={t('placeholder.quantity')}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">{stock.unit}</InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <label style={{ fontWeight: 600 }}>
                <span style={{ color: 'red', marginRight: 4 }}>*</span>
                {t('stock.history.quantity')}
              </label>
              <Controller
                name="quantity"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    sx={{ mt: 0.5 }}
                    placeholder={t('placeholder.quantity')}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">{stock.unit}</InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <label style={{ fontWeight: 600 }}>
                <span style={{ color: 'red', marginRight: 4 }}>*</span>
                {mode === 'product-issue' ? t('dialog.stock.remark') : t('dialog.stock.reasonQuantity')}
              </label>
              <Controller
                name="remark"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    sx={{ mt: 0.5 }}
                    placeholder={mode === 'edit-qty' ? t('dialog.stock.reason.placeholder') : ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          </>
        )}
        {/* ปุ่ม submit อยู่ใน CustomDialog แล้ว */}
      </form>
    </CustomDialog>
  )
}
